import { Draggable } from "@hello-pangea/dnd";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Button, ButtonBase, Checkbox, Collapse, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, sortBy } from "lodash";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { Doc } from "../../../common/js/icons";
import { noPhotoUrl } from "../utils/constants";
import { getFromAttributeValues } from "../utils/operations";
import schema from "./AuditSearchTables";

export const ListingIdLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecorationColor: "#2C99D2",
    },

    color: "#2C99D2",
    fontWeight: 600,
    textDecoration: "underline",
    textDecorationColor: "blue",
});

export const DocumentLinkButton = styled(ButtonBase)({
    color: "#2C99D2",
    cursor: "pointer",
    fontWeight: 600,
    textDecoration: "none",
});

const DocumentLinkSpan = styled("span")(({ document }) => {
    const mediaAssetKey = get(document, "mediaAssetKey");

    return {
        cursor: mediaAssetKey ? "pointer" : "default",
        color: mediaAssetKey ? "#2C99D2" : "grey",
        fontWeight: 600,
        textDecoration: "none",
    };
});

const AuditSearchRowContainer = styled("div")({
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: "1vw",
    fontSize: "13px",
});

const Lb = styled(Link)({
    textDecoration: "none",
});

const BrokerColumn = styled("div")({
    fontSize: "13px",
    marginLeft: "2px",
    "&>a:hover": {
        cursor: "default",
    },
});

const StyledTooltipSpan = styled("span")({
    "&>a:hover": {
        cursor: "default",
    },
});

const LightButton = styled(Button)({
    fontWeight: "400",
    minWidth: "0px",
    margin: "0px",
    marginRight: "5px",
    padding: "5px 10px",
});

const AuditSearchRow = (props) => {
    const { viewDocument, comp, i, activeTab, toggleCompAuditFlag } = props;

    const [expanded, setExpanded] = useState(false);
    const [sortedDocuments, setSortedDocuments] = useState([]);
    const [isChecked, setIsChecked] = useState(comp.audit_flg);

    useEffect(() => {
        loadDocuments(get(comp, "attachedRequiredDocuments"), get(comp, "missingRequiredDocuments"));
    }, []);

    useEffect(() => {
        setIsChecked(comp.audit_flg);
    }, [comp.audit_flg]);

    const loadDocuments = (attached, missing) => {
        let aggregatedDocs = undefined;

        if (attached && missing) {
            aggregatedDocs = [...attached, ...missing];
        } else if (attached) {
            aggregatedDocs = attached;
        } else if (missing) {
            aggregatedDocs = missing;
        }
        setSortedDocuments(sortBy(aggregatedDocs, ["documentTypeName", "documentTypeKey"]));
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        // change background colour if dragging
        background: isDragging ? "#C5C5C5" : "",

        // styles we need to apply on draggables
        ...draggableStyle,
    });

    const getTableCellFlex = (header) => {
        if (header === "Address") {
            return "2";
        } else if (header === "Documents") {
            return "5";
        } else {
            return "1";
        }
    };

    const getTableFixedWidth = (header) => {
        if (header === "Address") {
            return "200px";
        } else {
            return "100px";
        }
    };

    return (
        <Draggable draggableId={`AuditSearch Draggable - ${String(i)}`} index={i}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                >
                    <AuditSearchRowContainer container key={`tr-${i}`} selectable="false">
                        {schema.viewHeaders(activeTab)["all_comps"].map((y, k) => {
                            return y.name === "ToggleToList" ? (
                                <div
                                    style={{
                                        paddingLeft: "25px",
                                        flex: getTableCellFlex(y.name),
                                        minWidth: getTableFixedWidth(y.name),
                                        maxWidth: getTableFixedWidth(y.name),
                                    }}
                                >
                                    <Checkbox
                                        data-testid="auditorSearchCheckbox"
                                        checked={isChecked}
                                        color="primary"
                                        onChange={() => {
                                            toggleCompAuditFlag(comp.dealkey);
                                        }}
                                    />
                                    <IconButton
                                        aria-label="expand row"
                                        size="small"
                                        onClick={() => setExpanded(!expanded)}
                                    >
                                        {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                </div>
                            ) : y.name === "Property" ? (
                                <div
                                    key={`trc-${k}`}
                                    style={{
                                        flex: getTableCellFlex(y.name),
                                        marginLeft: "-50px",
                                        minWidth: getTableFixedWidth(y.name),
                                        maxWidth: getTableFixedWidth(y.name),
                                    }}
                                >
                                    <div
                                        className="propertySmallImageContainer"
                                        style={{
                                            width: "85px",
                                            maxWidth: "85px",
                                            height: "80px",
                                            margin: "10px 23px 10px 27px",
                                            backgroundImage: `url(${
                                                get(comp, "listing_photo", null) !== null
                                                    ? get(comp, "listing_photo", null)
                                                    : get(comp, "property_photo", null) !== null
                                                    ? get(comp, "property_photo", null)
                                                    : noPhotoUrl
                                            })`,
                                        }}
                                    ></div>
                                </div>
                            ) : y.name === "Address" ? (
                                <div
                                    key={`trc-${k}`}
                                    style={{
                                        fontSize: "13px",
                                        marginLeft: "24px",
                                        flex: getTableCellFlex(y.name),
                                        minWidth: getTableFixedWidth(y.name),
                                        maxWidth: getTableFixedWidth(y.name),
                                    }}
                                >
                                    <Lb to={`/deal/${comp.dealkey ? comp.dealkey : comp.dealKey}`}>
                                        <ListingIdLink data-testid="auditorSearchLink">
                                            {get(comp, "listing_name")}
                                        </ListingIdLink>
                                    </Lb>
                                    <div style={{ color: "red", fontWeight: "bold" }}>{`${
                                        getFromAttributeValues(comp, ["confidentialFlg", "confidential_flg"], false)
                                            ? "Confidential"
                                            : ""
                                    }`}</div>
                                    <div>{`${get(comp, "address1", {})},`}</div>
                                    <div>{`${get(comp, "city", {})}, ${get(comp, "state_code", "")} ${
                                        get(comp, "zip_postal_code", null) !== null ? get(comp, "zip_postal_code") : ""
                                    }`}</div>
                                    <div>{`${
                                        getFromAttributeValues(comp, ["confidentialFlg", "confidential_flg"], false)
                                            ? "Confidential "
                                            : ""
                                    } ${
                                        get(comp, "comp_status", false) === "OUTSIDE"
                                            ? "Outside"
                                            : get(comp, "comp_status", false) === "APPRAISAL"
                                            ? "Appraisal"
                                            : "KM"
                                    }, ${get(comp, "property_type")}`}</div>
                                </div>
                            ) : y.transform ? (
                                y.name === "Brokers" ? (
                                    <BrokerColumn
                                        style={{
                                            flex: getTableCellFlex(y.name),
                                            minWidth: getTableFixedWidth(y.name),
                                            maxWidth: getTableFixedWidth(y.name),
                                        }}
                                        key={`trc-${k}`}
                                    >
                                        {get(comp, y.prop, " ") && (
                                            <div>
                                                <StyledTooltipSpan
                                                    data-tooltip-id={`audit-search-tooltip-${k}`}
                                                    data-tooltip-content={
                                                        get(comp, y.prop, "").includes(",") ? get(comp, y.prop, "") : ""
                                                    }
                                                >
                                                    {y.transform(get(comp, y.prop, ""))}
                                                </StyledTooltipSpan>
                                                <Tooltip
                                                    id={`audit-search-tooltip-${k}`}
                                                    place="top"
                                                    style={{ backgroundColor: "#333", color: "#fff" }}
                                                />
                                            </div>
                                        )}
                                    </BrokerColumn>
                                ) : (
                                    // deal type
                                    <div
                                        key={`trc-${k}`}
                                        numeric={y.numeric}
                                        style={{
                                            fontSize: "13px",
                                            marginLeft: "4px",
                                            flex: getTableCellFlex(y.name),
                                            minWidth: getTableFixedWidth(y.name),
                                            maxWidth: getTableFixedWidth(y.name),
                                        }}
                                    >
                                        {y.transform(get(comp, y.prop))}
                                    </div>
                                )
                            ) : y.name === "Documents" ? (
                                <div
                                    style={{
                                        paddingTop: "30px",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "1vw",
                                        flex: getTableCellFlex(y.name)
                                    }}
                                >
                                    {sortedDocuments.map((document, document_index) => (
                                        <span>
                                            <span
                                                data-tooltip-id={`tr-${i}-doc-${document_index}`}
                                                data-testid={`${get(document, "documentTypeName", "")}`}
                                            >
                                                <LightButton
                                                    disabled={get(document, "mediaAssetKey", null) === null}
                                                    onClick={() => viewDocument(null, get(document, "mediaAssetKey"))}
                                                    color="primary"
                                                >
                                                    <Doc
                                                        fill={
                                                            get(document, "mediaAssetKey", null) !== null
                                                                ? "#2C99D2"
                                                                : "#9E9E9E"
                                                        }
                                                    />
                                                </LightButton>
                                            </span>
                                            <Tooltip
                                                id={`tr-${i}-doc-${document_index}`}
                                                place="top"
                                                className="statBoxTooltip"
                                                style={{
                                                    backgroundColor: "#333",
                                                    color: "#fff",
                                                    fontSize: "0.8rem",
                                                }}
                                            >
                                                {get(document, "documentTypeName", "")}
                                            </Tooltip>
                                        </span>
                                    ))}
                                </div>
                            ) : null;
                        })}
                    </AuditSearchRowContainer>
                    <Collapse in={expanded} timeout="auto">
                        <div style={{ borderTop: "1px solid rgb(212, 212, 212)" }}>
                            {sortedDocuments.map((document, document_index) => (
                                <div
                                    style={{
                                        fontSize: "13px",
                                        padding: `10px 10px 0px 0px`,
                                        borderBottom: "1px solid rgb(212, 212, 212)",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <span data-tooltip-id={`tr-${i}-expandedDoc-${document_index}`}>
                                        <LightButton
                                            disabled={get(document, "mediaAssetKey", null) === null}
                                            onClick={() => viewDocument(null, get(document, "mediaAssetKey"))}
                                            color="primary"
                                        >
                                            <Doc
                                                fill={
                                                    get(document, "mediaAssetKey", null) !== null
                                                        ? "#2C99D2"
                                                        : "#9E9E9E"
                                                }
                                            />
                                        </LightButton>
                                    </span>
                                    <DocumentLinkButton
                                        disabled={get(document, "mediaAssetKey", null) === null}
                                        onClick={() => viewDocument(null, get(document, "mediaAssetKey"))}
                                        color="primary"
                                    >
                                        <DocumentLinkSpan document={document}>
                                            {get(document, "documentTypeName", "")}
                                        </DocumentLinkSpan>
                                    </DocumentLinkButton>
                                </div>
                            ))}
                        </div>
                    </Collapse>
                </div>
            )}
        </Draggable>
    );
};

export default AuditSearchRow;
